import { useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type Primitive = string | number;
type UrlListState = Record<string, Primitive | Primitive[]>;

type UseUrlListStateOptions<T extends UrlListState> = {
  defaults: T;
  arrayKeys?: Array<keyof T>;
  intKeys?: Array<keyof T>;
};

const normalizeArray = (value: string | null) => {
  if (!value) return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

export function useUrlListState<T extends UrlListState>({
  defaults,
  arrayKeys = [],
  intKeys = [],
}: UseUrlListStateOptions<T>) {
  const location = useLocation();
  const navigate = useNavigate();
  const stateRef = useRef<T>({} as T);
  const searchRef = useRef(location.search);
  const pathnameRef = useRef(location.pathname);

  const state = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const nextState = {} as T;

    Object.keys(defaults).forEach((key) => {
      const typedKey = key as keyof T;
      const defaultValue = defaults[typedKey];

      if (arrayKeys.includes(typedKey)) {
        nextState[typedKey] = normalizeArray(params.get(key)) as T[keyof T];
        return;
      }

      if (intKeys.includes(typedKey)) {
        const rawValue = params.get(key);
        const parsed = rawValue ? Number.parseInt(rawValue, 10) : Number(defaultValue);
        nextState[typedKey] = (
          Number.isFinite(parsed) ? parsed : Number(defaultValue)
        ) as T[keyof T];
        return;
      }

      nextState[typedKey] = (params.get(key) ?? defaultValue) as T[keyof T];
    });

    return nextState;
  }, [arrayKeys, defaults, intKeys, location.search]);

  const updateState = useCallback(
    (updates: Partial<T> | ((prev: T) => Partial<T>)) => {
      const params = new URLSearchParams(searchRef.current);
      const resolvedUpdates = typeof updates === "function" ? updates(stateRef.current) : updates;
      const nextState = { ...stateRef.current };

      Object.entries(resolvedUpdates).forEach(([key, value]) => {
        const typedKey = key as keyof T;
        const defaultValue = defaults[typedKey];

        if (arrayKeys.includes(typedKey)) {
          const listValue = Array.isArray(value) ? value : [];
          nextState[typedKey] = listValue as T[keyof T];
          if (listValue.length === 0) {
            params.delete(key);
          } else {
            params.set(key, listValue.join(","));
          }
          return;
        }

        if (value === undefined || value === null || value === "" || value === defaultValue) {
          nextState[typedKey] = defaultValue;
          params.delete(key);
          return;
        }

        nextState[typedKey] = value as T[keyof T];
        params.set(key, String(value));
      });

      const nextSearch = params.toString() ? `?${params.toString()}` : "";
      stateRef.current = nextState;
      searchRef.current = nextSearch;

      navigate(
        {
          pathname: pathnameRef.current,
          search: nextSearch,
        },
        { replace: true }
      );
    },
    [arrayKeys, defaults, navigate]
  );

  const setValue = useCallback(
    <K extends keyof T>(key: K, value: T[K] | ((prev: T[K]) => T[K])) => {
      updateState(
        (prev) =>
          ({
            [key]:
              typeof value === "function" ? (value as (current: T[K]) => T[K])(prev[key]) : value,
          } as unknown as Partial<T>)
      );
    },
    [updateState]
  );

  stateRef.current = state;
  searchRef.current = location.search;
  pathnameRef.current = location.pathname;

  return { state, updateState, setValue };
}
